import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { ProgressBar } from "@app/components/shared/ProgressBar";
import { OrderSummary } from "@app/components/shared/OrderSummary";
import { buttonStyles, inputStyles, labelStyles, pageStyles } from "@/styles/sharedStyles";

interface Step2Props {
  onNext: (data: any) => void;
  onBack: (currentStepData?: any) => void;
  onStepClick?: (step: number) => void;
  stepData?: any;
  flowType?: "one-off" | "subscription";
}

// Get Stripe publishable key from API and detect test mode
const getStripePromise = async () => {
  try {
    const response = await fetch("/api/stripe/config");
    const config = await response.json();
    if (config.success) {
      return {
        stripe: await loadStripe(config.data.publishableKey),
        isTestMode: config.data.publishableKey.includes("_test_"),
      };
    }
    console.error("Stripe config error:", config.error);
    throw new Error(config.error || "Failed to load Stripe configuration");
  } catch (error) {
    console.error("Failed to fetch Stripe config:", error);
    throw new Error("Failed to load Stripe configuration");
  }
};

function PaymentForm({
  onNext,
  onBack,
  onStepClick,
  stepData,
  flowType = "one-off",
  isTestMode = false,
}: Step2Props & { isTestMode?: boolean }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentData, setPaymentData] = useState({
    firstName: stepData?.step2?.firstName || "",
    lastName: stepData?.step2?.lastName || "",
    phone: stepData?.step2?.phone || "",
    email: stepData?.step2?.email || "",
    notifications: stepData?.step2?.notifications || false,
  });

  // Update payment data when stepData changes (for data persistence)
  useEffect(() => {
    if (stepData?.step2) {
      setPaymentData({
        firstName: stepData.step2.firstName || "",
        lastName: stepData.step2.lastName || "",
        phone: stepData.step2.phone || "",
        email: stepData.step2.email || "",
        notifications: stepData.step2.notifications || false,
      });
    }
  }, [stepData]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showTestCards, setShowTestCards] = useState(false);
  const [bypassPayment, setBypassPayment] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    card: ''
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setPaymentData((prev) => ({ ...prev, [field]: value }));
    // Clear validation errors when user starts typing
    if (showValidationErrors && fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  // Test card data
  const testCards = [
    {
      name: "Visa Success",
      number: "4242424242424242",
      exp: "12/28",
      cvc: "123",
    },
    {
      name: "Visa Debit",
      number: "4000056655665556",
      exp: "12/28",
      cvc: "123",
    },
    {
      name: "Mastercard",
      number: "5555555555554444",
      exp: "12/28",
      cvc: "123",
    },
    {
      name: "Declined Card",
      number: "4000000000000002",
      exp: "12/28",
      cvc: "123",
    },
  ];

  const handleTestCardClick = async (testCard: (typeof testCards)[0]) => {
    try {
      // Copy card number to clipboard
      await navigator.clipboard.writeText(testCard.number);
      setPaymentError(
        `✅ Copied ${testCard.name} details! Card: ${testCard.number}, Exp: ${testCard.exp}, CVC: ${testCard.cvc}`
      );
      setTimeout(() => setPaymentError(null), 8000);
    } catch (err) {
      // Fallback if clipboard API fails
      setPaymentError(
        `📋 ${testCard.name}: ${testCard.number} | ${testCard.exp} | ${testCard.cvc}`
      );
      setTimeout(() => setPaymentError(null), 10000);
    }
  };

  // Validation logic with error messages
  const validateForm = (showErrors = false) => {
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      card: ''
    };
    let isValid = true;

    // Check first name - should be non-empty and at least 2 characters
    if (!paymentData.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    } else if (paymentData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
      isValid = false;
    }
    
    // Check last name - should be non-empty and at least 2 characters
    if (!paymentData.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    } else if (paymentData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
      isValid = false;
    }
    
    // Check email format
    if (!paymentData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(paymentData.email)) {
        errors.email = 'Please enter a valid email address';
        isValid = false;
      }
    }
    
    // Check phone - simplified validation
    if (!paymentData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else {
      const cleanPhone = paymentData.phone.replace(/[\s\-\(\)]/g, '');
      if (cleanPhone.length < 10) {
        errors.phone = 'Phone number must be at least 10 digits';
        isValid = false;
      }
    }

    // Check notifications checkbox
    if (!paymentData.notifications) {
      isValid = false;
    }

    // Check card completion
    if (!cardComplete && !(isTestMode && bypassPayment)) {
      errors.card = 'Please enter complete card information';
      isValid = false;
    }
    
    if (showErrors) {
      setFieldErrors(errors);
      setShowValidationErrors(true);
    }
    
    return isValid;
  };

  const isFormValid = validateForm();

  const handlePayClick = () => {
    // Always show validation errors when button is clicked
    if (!validateForm(true)) {
      return;
    }

    // Check additional validation conditions
    if (!paymentData.notifications) {
      setFieldErrors(prev => ({ ...prev, notifications: 'You must agree to receive notifications' }));
      setShowValidationErrors(true);
      return;
    }

    if (!cardComplete && (!isTestMode || !bypassPayment)) {
      setFieldErrors(prev => ({ ...prev, card: 'Please enter valid card information' }));
      setShowValidationErrors(true);
      return;
    }

    if (isProcessing) {
      return;
    }

    handlePay();
  };

  const handlePay = async () => {
    setIsProcessing(true);
    setPaymentError(null);

    try {
      // If bypass is enabled in test mode, skip Stripe processing but still call backend
      if (isTestMode && bypassPayment) {
        // Simulate processing delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Call backend to create contact and store form data, but bypass Stripe
        const response = await fetch("/api/payment/process", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            flowType,
            recipientCount,
            customerInfo: {
              firstName: paymentData.firstName,
              lastName: paymentData.lastName,
              email: paymentData.email,
              phone: paymentData.phone,
            },
            paymentMethodId: "pm_bypass_test", // Special bypass payment method ID
            formData: stepData, // Include all form data for storage
            bypassPayment: true, // Flag to indicate bypass mode
          }),
        });

        const result = await response.json();

        if (result.success) {
          onNext({
            ...paymentData,
            paymentIntentId: result.data.paymentIntentId || "pi_test_bypass_" + Date.now(),
            paymentStatus: "succeeded",
            amount: totalPrice * 100, // Convert to cents
            bypassed: true,
          });
        } else {
          setPaymentError(result.error || "Bypass payment failed");
        }
        return;
      }

      // Normal Stripe payment flow
      if (!stripe || !elements) {
        setPaymentError("Payment system not ready");
        return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setPaymentError("Card element not found");
        return;
      }

      // Create payment method on frontend
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${paymentData.firstName} ${paymentData.lastName}`,
          email: paymentData.email,
          phone: paymentData.phone,
        },
      });

      if (error) {
        setPaymentError(error.message || "Failed to create payment method");
        return;
      }

      // Send payment method ID to backend for processing
      const response = await fetch("/api/payment/process", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          flowType,
          recipientCount,
          customerInfo: {
            firstName: paymentData.firstName,
            lastName: paymentData.lastName,
            email: paymentData.email,
            phone: paymentData.phone,
          },
          paymentMethodId: paymentMethod.id,
          formData: stepData, // Include all form data for storage
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Payment successful
        onNext({
          ...paymentData,
          paymentIntentId: result.data.paymentIntentId,
          paymentStatus: "succeeded",
          amount: result.data.amount,
        });
      } else {
        setPaymentError(result.error || "Payment failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setPaymentError("Payment processing failed");
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate pricing based on flow type and recipients
  const isSubscription = flowType === "subscription";
  const recipients = isSubscription ? [] : (stepData?.step1?.recipients || []);
  const recipientCount = isSubscription ? 1 : recipients.length;

  // Pricing logic based on wireframes
  const pricePerRecipient = 50; // $50 per recipient for one-off
  const subscriptionPrice = 250; // Flat $250 for subscription
  const totalPrice = isSubscription
    ? subscriptionPrice
    : pricePerRecipient * recipientCount;

  return (
    <div className="max-w-7xl w-full mx-auto mb-13">
      <div className="mb-8">
        <ProgressBar currentStep={2} totalSteps={4} onStepClick={onStepClick} />
      </div>

      <div className="grid lg:grid-cols-[350px_1fr] gap-8">
        <OrderSummary flowType={flowType} stepData={stepData} showSecure={true} />

        <div className="">
          <div className="mb-8">
            <h1 className={`${pageStyles.title} text-left max-w-2xl mx-auto`}>Start Gifting</h1>
            <p className="text-purple-medium max-w-2xl mx-auto text-left">
              {isSubscription
                ? "After your payment, you'll choose how you'd like to continue: schedule a quick call with your concierge or fill out the recipient and event details yourself. From there, we'll create a personalized gift list—thoughtfully curated by a real human, not an algorithm—based on your input. It's all designed to make your gift-giving truly special and effortless."
                : "After your payment, you'll schedule a quick call with your concierge. From there, we'll get to work on a personalized list of gift ideas curated just for your loved one—thoughtfully selected by a real human (not an algorithm). We'll take your input and turn it into something truly special."}
            </p>
          </div>

          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`form-label ${labelStyles.required}`}>
                  Your Name
                </label>
                <input
                  type="text"
                  value={paymentData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  placeholder="Your Name"
                  className={`${inputStyles.base} ${showValidationErrors && fieldErrors.firstName ? inputStyles.error : ''}`}
                />
                {showValidationErrors && fieldErrors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.firstName}</p>
                )}
              </div>

              <div>
                <label className={`form-label ${labelStyles.required}`}>
                  Your Last name
                </label>
                <input
                  type="text"
                  value={paymentData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  placeholder="Your Last Name"
                  className={`${inputStyles.base} ${showValidationErrors && fieldErrors.lastName ? inputStyles.error : ''}`}
                />
                {showValidationErrors && fieldErrors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.lastName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`form-label ${labelStyles.required}`}>
                  Your Phone Number
                </label>
                <input
                  type="tel"
                  value={paymentData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="(555) 123 - 4567"
                  className={`${inputStyles.base} ${showValidationErrors && fieldErrors.phone ? inputStyles.error : ''}`}
                />
                {!(showValidationErrors && fieldErrors.phone) && (
                  <p className="text-sm text-gray-500 mt-1">Phone number must be at least 10 digits</p>
                )}
                {showValidationErrors && fieldErrors.phone && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <label className={`form-label ${labelStyles.required}`}>
                  Your Email
                </label>
                <input
                  type="email"
                  value={paymentData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your@email.com"
                  className={`${inputStyles.base} ${showValidationErrors && fieldErrors.email ? inputStyles.error : ''}`}
                />
                {showValidationErrors && fieldErrors.email && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.email}</p>
                )}
              </div>
            </div>

        {paymentError && (
          <div
            className={`px-4 py-3 rounded-xl ${
              paymentError.startsWith("✅")
                ? "bg-green-100 border border-green-400 text-green-700"
                : paymentError.startsWith("📋")
                ? "bg-blue-100 border border-blue-400 text-blue-700"
                : "bg-red-100 border border-red-400 text-red-700"
            }`}
          >
            <p className="text-sm">{paymentError}</p>
          </div>
        )}

            {/* Card Information */}
            <div>
              <label className={`form-label ${labelStyles.required}`}>
                Card Number
              </label>
              <div className={`${inputStyles.base} ${showValidationErrors && fieldErrors.card ? inputStyles.error : ''}`}>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#374151",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        "::placeholder": {
                          color: "#9CA3AF",
                        },
                      },
                    },
                    hidePostalCode: true,
                  }}
                  onChange={(event) => {
                    setCardComplete(event.complete);
                    if (event.error) {
                      setFieldErrors(prev => ({ ...prev, card: event.error?.message || 'Card validation error' }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, card: '' }));
                    }
                  }}
                />
              </div>
              {showValidationErrors && fieldErrors.card && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.card}</p>
              )}

        {isTestMode && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-blue-800 font-semibold">
                  🧪 Test Mode Detected
                </p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-blue-700 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={showTestCards}
                        onChange={(e) => setShowTestCards(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-10 h-6 rounded-full transition-colors duration-200 flex items-center ${
                        showTestCards ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          showTestCards ? 'translate-x-5' : 'translate-x-1'
                        }`}></div>
                      </div>
                    </div>
                    Show test cards
                  </label>
                </div>
              </div>

              {showTestCards && (
                <div className="space-y-3">
                  <p className="text-xs text-blue-700 mb-2">
                    Click a test card to copy its details:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {testCards.map((card, index) => (
                      <button
                        key={index}
                        onClick={() => handleTestCardClick(card)}
                        className="text-left p-3 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 border border-blue-300 hover:border-blue-400 cursor-pointer hover:shadow-sm hover:scale-105"
                      >
                        <div className="text-sm font-medium text-blue-900">
                          {card.name}
                        </div>
                        <div className="text-xs text-blue-700 font-mono">
                          {card.number}
                        </div>
                        <div className="text-xs text-blue-600">
                          {card.exp} • {card.cvc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-blue-600 mt-3">
                Your payment will be processed securely through Stripe. In test
                mode, no real charges are made.
              </p>
            </div>
            <div className="my-4">
              <label className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={bypassPayment}
                    onChange={(e) => setBypassPayment(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-12 h-7 rounded-full transition-colors duration-200 flex items-center ${
                    bypassPayment ? 'bg-green-600' : 'bg-gray-300'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                      bypassPayment ? 'translate-x-6' : 'translate-x-1'
                    }`}></div>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-green-800">
                    🚀 Bypass Stripe Payment
                  </div>
                  <div className="text-xs text-green-700">
                    Skip actual payment processing and proceed directly to next
                    step
                  </div>
                </div>
              </label>
            </div>
          </>
        )}

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="notifications"
                checked={paymentData.notifications}
                onChange={(e) => handleInputChange("notifications", e.target.checked)}
                className="w-5 h-5 text-green-medium border-gray-300 rounded focus:ring-green-medium mt-0.5"
              />
              <label htmlFor="notifications" className={`text-sm text-gray-700 mb-4 ${labelStyles.required}`}>
                I agree to receive notifications and alerts
              </label>
            </div>

            <button
              className={`${isFormValid && paymentData.notifications && (cardComplete || (isTestMode && bypassPayment)) && !isProcessing
                ? buttonStyles.primary
                : buttonStyles.disabled
              } w-full!`}
              onClick={handlePayClick}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                 {isTestMode && bypassPayment
                    ? "Bypassing Payment..."
                    : "Processing Payment..."}
                </span>
              ) : !stripe || !elements ? (
                "Loading Payment..."
              ) : isTestMode && bypassPayment ? (
                `🚀 BYPASS & CONTINUE ($${totalPrice})`
              ) : (
                `PAY $${totalPrice}`
              )}
            </button>
            
            {/* Processing Warning Message */}
            {isProcessing && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Please don't close this page
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Your payment is being processed. This may take a few seconds to a couple of minutes. Closing this page may result in payment issues.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

export function Step2(props: Step2Props) {
  const [stripePromise, setStripePromise] = useState<any | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getStripePromise()
      .then((result) => {
        setStripePromise(result.stripe);
        setIsTestMode(result.isTestMode);
        setLoadingError(null);
      })
      .catch((error) => {
        console.error("Stripe loading error:", error);
        setLoadingError(error.message);
        // In test mode or development, allow bypass
        if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
          setIsTestMode(true);
          setStripePromise(null); // Will trigger bypass mode
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className="max-w-2xl w-full mx-auto bg-white rounded-3xl shadow-sm p-8">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-purple-medium rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment system...</p>
        </div>
      </div>
    );
  }

  // If there's an error but we're in development, show error but allow bypass
  if (loadingError && !stripePromise) {
    return (
      <div className="max-w-2xl w-full mx-auto bg-white rounded-3xl shadow-sm p-8">
        <div className="text-center mb-6">
          <div className="text-red-600 mb-4">
            <p className="font-semibold">Payment System Error</p>
            <p className="text-sm">{loadingError}</p>
          </div>
         {isTestMode && (
            <div className="bg-yellow-100 border border-yellow-400 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                Development mode detected. Proceeding with payment bypass.
              </p>
            </div>
          )}
        </div>
        <PaymentForm {...props} isTestMode={isTestMode} />
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} isTestMode={isTestMode} />
    </Elements>
  );
}
